// src/components/DrawingComponent.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { UserCircleIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

const DrawingComponent = ({ proposal }) => {
  const [votes, setVotes] = useState([]);
  //   const [isLoading, setIsLoading] = useState(true);
  const [winners, setWinners] = useState([]);
  const [numWinners, setNumWinners] = useState(1);
  const [hasDrawn, setHasDrawn] = useState(false);
  //   const [error, setError] = useState(null);

  // 投票データを取得
  useEffect(() => {
    const fetchVotes = async () => {
      try {
        // setIsLoading(true);

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/votes/proposal/${proposal.id}`
        );
        setVotes(response.data);

        // setIsLoading(false);
      } catch (error) {
        console.error('投票取得エラー:', error);
        // setError('投票データの取得に失敗しました');
        // setIsLoading(false);
      }
    };

    fetchVotes();
  }, [proposal.id]);

  // Function to perform the random drawing
  const performDrawing = () => {
    if (votes.length === 0 || numWinners <= 0) return;

    // Make a copy of the votes array
    const votesCopy = [...votes];
    // Shuffle the array using Fisher-Yates algorithm
    for (let i = votesCopy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [votesCopy[i], votesCopy[j]] = [votesCopy[j], votesCopy[i]];
    }

    // Select the number of winners requested (or all voters if there are fewer than requested)
    const selectedWinners = votesCopy.slice(0, Math.min(numWinners, votesCopy.length));
    setWinners(selectedWinners);
    setHasDrawn(true);
  };

  // Prepare data for CSV export of winners
  const exportWinnersCSV = () => {
    const headers = 'Rank,Address,Choice,VotingPower,VotedAt\n';
    const rows = winners
      .map((winner, index) => {
        return `${index + 1},${winner.voterAddress},"${winner.choice?.text || 'Unknown'}",${
          winner.weight
        },${new Date(winner.createdAt).toLocaleString()}`;
      })
      .join('\n');

    const csvData = headers + rows;
    downloadCSV(csvData, `${proposal.title}_winners.csv`);
  };

  // Prepare data for CSV export of all voters
  const exportAllVotersCSV = () => {
    const headers = 'Number,Address,Choice,VotingPower,VotedAt\n';
    const rows = votes
      .map((vote, index) => {
        return `${index + 1},${vote.voterAddress},"${vote.choice?.text || 'Unknown'}",${
          vote.weight
        },${new Date(vote.createdAt).toLocaleString()}`;
      })
      .join('\n');

    const csvData = headers + rows;
    downloadCSV(csvData, `${proposal.title}_all_voters.csv`);
  };

  // Helper function to download CSV data
  const downloadCSV = (csvData, filename) => {
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Shorten address display
  const shortenAddress = address => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mt-6">
      <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">投票者から抽選</h2>

      {!hasDrawn ? (
        <div>
          <div className="mb-4">
            <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
              当選者数
            </label>
            <input
              type="number"
              min="1"
              max={votes.length}
              value={numWinners}
              onChange={e =>
                setNumWinners(Math.max(1, Math.min(votes.length, parseInt(e.target.value) || 1)))
              }
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 w-full"
            />
          </div>

          <button
            onClick={performDrawing}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition disabled:bg-blue-300 dark:disabled:bg-blue-800"
            disabled={votes.length === 0}
          >
            抽選を実行
          </button>

          {votes.length === 0 && (
            <p className="text-red-500 dark:text-red-400 mt-2 text-sm">
              抽選するには少なくとも1票以上の投票が必要です。
            </p>
          )}
        </div>
      ) : (
        <div>
          <h3 className="text-lg font-medium mb-4 text-gray-800 dark:text-gray-200">抽選結果</h3>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    順位
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    アドレス
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    選択肢
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    投票力
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {winners.map((winner, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {index + 1}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm">
                      <div className="flex items-center">
                        <div className="h-6 w-6 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-300 mr-2">
                          <UserCircleIcon className="h-4 w-4" />
                        </div>
                        <Link
                          to={`/user/${winner.voterAddress}`}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                        >
                          {shortenAddress(winner.voterAddress)}
                        </Link>
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {winner.choice.text}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 text-right">
                      {winner.weight}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-wrap gap-2 justify-between">
            <button
              onClick={() => setHasDrawn(false)}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition"
            >
              再抽選
            </button>

            <div className="flex gap-2">
              <button
                onClick={exportWinnersCSV}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                当選者CSV
              </button>

              <button
                onClick={exportAllVotersCSV}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                全投票者CSV
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DrawingComponent;
